import { normalizeExecutor } from "./executors";
import type {
  Activity,
  AgentTask,
  FieldOutbox,
  LocalState,
  StatusOutbox,
  SyncResponse,
  Topic,
  TopicStatus,
} from "../types";
import { mergeById } from "./merge";
import { isAfter } from "./time";

export type StatusDecision =
  | { action: "patch" }
  | { action: "already" }
  | { action: "conflict"; remoteStatus: TopicStatus; remoteLastEditedTime: string }
  | { action: "missing" };

export function decideStatusWrite(input: {
  requested: TopicStatus;
  baseLastEditedTime: string;
  remote: { status: TopicStatus; lastEditedTime: string } | null;
}): StatusDecision {
  if (!input.remote) return { action: "missing" };
  if (!isAfter(input.remote.lastEditedTime, input.baseLastEditedTime)) {
    return input.remote.status === input.requested ? { action: "already" } : { action: "patch" };
  }
  if (input.remote.status === input.requested) return { action: "already" };
  return {
    action: "conflict",
    remoteStatus: input.remote.status,
    remoteLastEditedTime: input.remote.lastEditedTime,
  };
}

export type FieldDecision = { action: "patch" } | { action: "conflict" } | { action: "local_only" };

export function decideFieldWrite(input: {
  baseLastEditedTime: string;
  remoteLastEditedTime: string | null;
  writesNotion: boolean;
}): FieldDecision {
  if (!input.writesNotion) return { action: "local_only" };
  if (input.remoteLastEditedTime && isAfter(input.remoteLastEditedTime, input.baseLastEditedTime)) {
    return { action: "conflict" };
  }
  return { action: "patch" };
}

export function conflictMessage(remoteStatus: TopicStatus): string {
  return `Notion 在这之后更新了选题，已保留 Notion 的状态「${remoteStatus}」。`;
}

export function statusChangeMessage(fromStatus: TopicStatus, toStatus: TopicStatus): string {
  return `状态：${fromStatus} → ${toStatus}`;
}

function overlayTopic(topic: Topic, item: StatusOutbox | FieldOutbox): Topic {
  if (item.kind === "set_status") {
    return { ...topic, status: item.status, syncError: item.lastError };
  }
  return {
    ...topic,
    publishUrl: item.publishUrl === undefined ? topic.publishUrl : item.publishUrl,
    plannedPublishDate:
      item.plannedPublishDate === undefined ? topic.plannedPublishDate : item.plannedPublishDate,
    platforms: item.platforms === undefined ? topic.platforms : item.platforms,
    notes: item.notes === undefined ? topic.notes : item.notes,
    queueOrder: item.queueOrder === undefined ? topic.queueOrder : item.queueOrder,
    syncError: item.lastError,
  };
}

export function applySyncResponse(
  previous: LocalState,
  sentOutboxIds: readonly string[],
  sentSnapshots: LocalState["snapshots"],
  sentPosts: LocalState["postMetrics"],
  sentActivities: Activity[],
  response: SyncResponse,
  sentTasks: LocalState["tasks"] = [],
  sentAccounts: LocalState["accounts"] = [],
  sentBenchmarks: LocalState["benchmarks"] = [],
  sentGuides: LocalState["styleGuides"] = [],
  sentScores: LocalState["postScores"] = [],
  sentSettings: LocalState["scoreSettings"] = [],
): LocalState {
  const sent = new Set(sentOutboxIds);
  const results = new Map(response.results.map((item) => [item.outboxId, item]));
  const droppedActivities = new Set<string>();

  const outbox = previous.outbox.flatMap((item) => {
    if (!sent.has(item.id)) return [item];
    const result = results.get(item.id);
    if (!result) return [item];
    const editedWhileSending = result.updatedAt != null && result.updatedAt !== item.updatedAt;
    if (editedWhileSending) {
      const remote = response.topics.find((topic) => topic.id === item.topicId);
      return [remote ? { ...item, baseLastEditedTime: remote.lastEditedTime, lastError: null } : item];
    }
    if (result.conflict) {
      if (item.kind === "set_status") droppedActivities.add(item.activityId);
      return [];
    }
    if (result.ok) return [];
    if (item.kind === "set_status" || item.kind === "patch_fields") {
      return [{ ...item, lastError: result.error ?? "同步失败" }];
    }
    return [item];
  });

  let topics = response.topics.map((topic) => ({ ...topic }));
  for (const item of outbox) {
    topics = topics.map((topic) => (topic.id === item.topicId ? overlayTopic(topic, item) : topic));
  }

  const pendingActivityIds = new Set(
    outbox.flatMap((item) => (item.kind === "set_status" ? [item.activityId] : [])),
  );
  const keptLocalActivities = previous.activities.filter(
    (activity) => pendingActivityIds.has(activity.id) && !droppedActivities.has(activity.id),
  );
  const sentActivityMap = new Map(sentActivities.map((activity) => [activity.id, activity]));
  const newerActivities = previous.activities.filter((activity) => {
    const sentActivity = sentActivityMap.get(activity.id);
    return sentActivity ? activity.updatedAt > sentActivity.updatedAt : !sentActivityMap.has(activity.id) && !pendingActivityIds.has(activity.id);
  });
  const activities = mergeById(response.activities, [...keptLocalActivities, ...newerActivities]).filter(
    (activity) => !droppedActivities.has(activity.id),
  );

  const snapshots = mergeById(response.snapshots, rowsChangedSince(sentSnapshots, previous.snapshots));
  const postMetrics = mergeById(response.postMetrics, rowsChangedSince(sentPosts, previous.postMetrics));
  const tasks = response.tasks
    ? keepTaskFields(mergeById(response.tasks, rowsChangedSince(sentTasks, previous.tasks ?? [])), previous.tasks ?? [])
    : previous.tasks ?? [];
  const accounts = response.accounts
    ? mergeById(response.accounts, rowsChangedSince(sentAccounts, previous.accounts ?? []))
    : previous.accounts ?? [];
  const benchmarks = response.benchmarks
    ? mergeById(response.benchmarks, rowsChangedSince(sentBenchmarks, previous.benchmarks ?? []))
    : previous.benchmarks ?? [];
  const styleGuides = response.styleGuides
    ? mergeById(response.styleGuides, rowsChangedSince(sentGuides, previous.styleGuides ?? []))
    : previous.styleGuides ?? [];
  const postScores = response.postScores
    ? mergeById(response.postScores, rowsChangedSince(sentScores, previous.postScores ?? []))
    : previous.postScores ?? [];
  const scoreSettings = response.scoreSettings
    ? mergeById(response.scoreSettings, rowsChangedSince(sentSettings, previous.scoreSettings ?? []))
    : previous.scoreSettings ?? [];
  const executors = Array.isArray(response.executors)
    ? response.executors.flatMap((item) => {
        const executor = normalizeExecutor(item);
        return executor ? [executor] : [];
      })
    : previous.executors ?? [];

  return { topics, activities, snapshots, postMetrics, tasks, accounts, benchmarks, styleGuides, postScores, scoreSettings, executors, outbox };
}

function keepTaskFields(remote: AgentTask[], local: AgentTask[]): AgentTask[] {
  const previous = new Map(local.map((task) => [task.id, task]));
  return remote.map((task) => {
    const earlier = previous.get(task.id);
    const weights = Array.isArray(task.weights) ? task.weights : [];
    const sameTime = earlier != null && earlier.updatedAt === task.updatedAt;
    const keptWeights = weights.length === 0 && sameTime && earlier.weights.length > 0 ? earlier.weights : weights;
    const subjectId = task.subjectId ?? (sameTime ? earlier.subjectId : null);
    const claimedBy = task.claimedBy ?? (sameTime ? earlier.claimedBy : null);
    if (keptWeights === task.weights && subjectId === task.subjectId && claimedBy === task.claimedBy) return task;
    return { ...task, weights: keptWeights, subjectId, claimedBy };
  });
}

function rowsChangedSince<T extends { id: string; updatedAt: string }>(sent: T[], current: T[]): T[] {
  const sentMap = new Map(sent.map((item) => [item.id, item]));
  return current.filter((item) => {
    const previous = sentMap.get(item.id);
    if (!previous) return true;
    return item.updatedAt > previous.updatedAt;
  });
}

export function emptyLocalState(): LocalState {
  return {
    topics: [],
    activities: [],
    snapshots: [],
    postMetrics: [],
    tasks: [],
    accounts: [],
    benchmarks: [],
    styleGuides: [],
    postScores: [],
    scoreSettings: [],
    executors: [],
    outbox: [],
  };
}
